/**
 * Pat Nutrition Theme - JavaScript
 * Performance-optimized, minimal JS
 * No jQuery, no external dependencies
 */

(function() {
  'use strict';

  // ==========================================================================
  // Utility Functions
  // ==========================================================================

  const debounce = (fn, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn(...args), delay);
    };
  };

  // Check if SparkLayer B2B is active (user is authenticated B2B customer)
  const isSparkLayerActive = () => {
    return typeof window.sparkOptions !== 'undefined' && window.sparkOptions.auth;
  };

  const fetchConfig = (type = 'json') => ({
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': `application/${type}`
    }
  });

  // ==========================================================================
  // Mobile Menu
  // ==========================================================================

  const initMobileMenu = () => {
    const menu = document.querySelector('[data-mobile-menu]');
    const toggleBtn = document.querySelector('[data-mobile-menu-toggle]');
    const closeBtn = document.querySelector('[data-mobile-menu-close]');
    const overlay = document.querySelector('[data-mobile-menu-overlay]');

    if (!menu || !toggleBtn) return;

    const open = () => {
      menu.classList.add('is-open');
      menu.setAttribute('aria-hidden', 'false');
      toggleBtn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    };

    const close = () => {
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      toggleBtn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    };

    toggleBtn.addEventListener('click', open);
    closeBtn?.addEventListener('click', close);
    overlay?.addEventListener('click', close);

    // Submenu toggles
    document.querySelectorAll('[data-mobile-submenu-toggle]').forEach(btn => {
      btn.addEventListener('click', () => {
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', !expanded);
        const submenu = btn.nextElementSibling;
        submenu?.classList.toggle('is-open');
      });
    });

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && menu.classList.contains('is-open')) {
        close();
      }
    });
  };

  // ==========================================================================
  // Cart Drawer
  // ==========================================================================

  const initCartDrawer = () => {
    // B2B users use SparkLayer cart drawer - skip regular cart drawer
    if (isSparkLayerActive()) {
      console.log('SparkLayer B2B active - skipping Shopify cart drawer init');
      return;
    }

    const drawer = document.querySelector('[data-cart-drawer]');
    const toggleBtns = document.querySelectorAll('[data-cart-toggle]');
    const overlay = document.querySelector('[data-cart-drawer-overlay]');

    if (!drawer) return;

    const open = () => {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    };

    const close = () => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };

    // Toggle buttons
    toggleBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        open();
      });
    });

    // Close button - use event delegation for dynamically loaded content
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-drawer-close]')) {
        close();
      }
    });

    overlay?.addEventListener('click', close);

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        close();
      }
    });

    window.cartDrawer = { open, close };
  };

  // ==========================================================================
  // Cart Functions
  // ==========================================================================

  const updateCartCount = async () => {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
        el.classList.toggle('hidden', cart.item_count === 0);
      });
      
      return cart;
    } catch (error) {
      console.error('Failed to update cart count:', error);
    }
  };

  const refreshCartDrawer = async () => {
    try {
      const response = await fetch('/?section_id=cart-drawer');
      const html = await response.text();
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newDrawer = doc.querySelector('[data-cart-drawer]');
      const currentDrawer = document.querySelector('[data-cart-drawer]');
      
      if (newDrawer && currentDrawer) {
        // Preserve open state
        const wasOpen = currentDrawer.classList.contains('is-open');
        currentDrawer.innerHTML = newDrawer.innerHTML;
        if (wasOpen) {
          currentDrawer.classList.add('is-open');
          currentDrawer.setAttribute('aria-hidden', 'false');
        }
      }
    } catch (error) {
      console.error('Failed to refresh cart drawer:', error);
    }
  };

  const addToCart = async (variantId, quantity = 1, properties = {}) => {
    // B2B users use SparkLayer cart - don't use Shopify cart directly
    if (isSparkLayerActive()) {
      console.log('SparkLayer B2B active - skipping Shopify cart');
      return;
    }

    try {
      const itemData = {
        id: variantId,
        quantity: quantity
      };
      
      // Add properties if provided (for bundle selections, etc.)
      if (properties && Object.keys(properties).length > 0) {
        itemData.properties = properties;
      }

      const response = await fetch('/cart/add.js', {
        ...fetchConfig(),
        body: JSON.stringify({
          items: [itemData]
        })
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      const data = await response.json();
      
      // Update cart count
      await updateCartCount();
      
      // Refresh and open cart drawer (B2C only)
      await refreshCartDrawer();
      if (window.cartDrawer) {
        window.cartDrawer.open();
      }

      showNotification('Toegevoegd aan winkelwagen!', 'success');
      return data;
    } catch (error) {
      console.error(error);
      showNotification('Kon niet toevoegen aan winkelwagen', 'error');
      throw error;
    }
  };

  const updateCartItem = async (key, quantity) => {
    try {
      const response = await fetch('/cart/change.js', {
        ...fetchConfig(),
        body: JSON.stringify({ id: key, quantity })
      });

      if (!response.ok) throw new Error('Failed to update cart');

      const cart = await response.json();
      
      // Update cart count
      await updateCartCount();
      
      // Refresh cart drawer
      await refreshCartDrawer();
      
      return cart;
    } catch (error) {
      console.error('Cart update failed:', error);
      showNotification('Kon winkelwagen niet bijwerken', 'error');
      throw error;
    }
  };

  // ==========================================================================
  // Upsell Helper - Add product by handle
  // ==========================================================================

  const addUpsellToCart = async (productHandle, quantity = 1) => {
    if (!productHandle) return null;

    try {
      // Fetch product data by handle
      const response = await fetch(`/products/${productHandle}.js`);
      
      if (!response.ok) {
        console.warn(`[Upsell] Product not found: ${productHandle}`);
        return null;
      }

      const product = await response.json();
      
      // Get first available variant
      const variant = product.variants.find(v => v.available) || product.variants[0];
      
      if (!variant) {
        console.warn(`[Upsell] No variant available for: ${productHandle}`);
        return null;
      }

      // Add to cart silently (no notification, no drawer refresh yet)
      const addResponse = await fetch('/cart/add.js', {
        ...fetchConfig(),
        body: JSON.stringify({
          items: [{
            id: variant.id,
            quantity: quantity
          }]
        })
      });

      if (!addResponse.ok) {
        console.warn(`[Upsell] Failed to add to cart: ${productHandle}`);
        return null;
      }

      const data = await addResponse.json();
      console.log(`[Upsell] Added: ${product.title}`);
      return data;

    } catch (error) {
      console.warn(`[Upsell] Error adding product: ${productHandle}`, error);
      return null;
    }
  };

  // ==========================================================================
  // Product Card AJAX Add to Cart
  // ==========================================================================

  const initProductCardCart = () => {
    // B2B users use SparkLayer - skip B2C add-to-cart
    if (isSparkLayerActive()) return;

    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('.product-card__cart-form');
      if (!form) return;
      
      e.preventDefault();
      
      const btn = form.querySelector('.product-card__cart-btn');
      const variantId = form.querySelector('input[name="id"]')?.value;
      const quantity = parseInt(form.querySelector('input[name="quantity"]')?.value) || 1;
      
      if (!variantId) return;
      
      // Disable button
      if (btn) {
        btn.disabled = true;
        btn.classList.add('is-loading');
      }
      
      try {
        await addToCart(variantId, quantity);
      } finally {
        if (btn) {
          btn.disabled = false;
          btn.classList.remove('is-loading');
        }
      }
    });
  };

  // ==========================================================================
  // Cart Drawer Item Updates
  // ==========================================================================

  const initCartDrawerItems = () => {
    // B2B users use SparkLayer cart - skip Shopify cart drawer items
    if (isSparkLayerActive()) return;

    // Quantity buttons in cart drawer
    document.addEventListener('click', async (e) => {
      const minusBtn = e.target.closest('[data-cart-quantity] [data-quantity-minus]');
      const plusBtn = e.target.closest('[data-cart-quantity] [data-quantity-plus]');
      const removeBtn = e.target.closest('[data-remove-item]');
      
      if (minusBtn) {
        const key = minusBtn.dataset.key;
        const input = minusBtn.parentElement.querySelector('[data-quantity-input]');
        const currentQty = parseInt(input?.value) || 1;
        const newQty = Math.max(0, currentQty - 1);
        
        if (input) input.value = newQty;
        await updateCartItem(key, newQty);
      }
      
      if (plusBtn) {
        const key = plusBtn.dataset.key;
        const input = plusBtn.parentElement.querySelector('[data-quantity-input]');
        const currentQty = parseInt(input?.value) || 1;
        const newQty = currentQty + 1;
        
        if (input) input.value = newQty;
        await updateCartItem(key, newQty);
      }
      
      if (removeBtn) {
        e.preventDefault();
        const key = removeBtn.dataset.key;
        await updateCartItem(key, 0);
      }
    });
    
    // Direct input change in cart drawer
    document.addEventListener('change', debounce(async (e) => {
      const input = e.target.closest('[data-cart-quantity] [data-quantity-input]');
      if (!input) return;
      
      const key = input.dataset.key;
      const quantity = parseInt(input.value) || 0;
      
      await updateCartItem(key, quantity);
    }, 500));
  };

  // ==========================================================================
  // Predictive Search (PATNUTRITION.BE STYLE - 6 products instant)
  // ==========================================================================

  const initPredictiveSearch = () => {
    const searchContainers = document.querySelectorAll('[data-search-container]');
    
    searchContainers.forEach(container => {
      const input = container.querySelector('[data-search-input]');
      const predictiveSearch = container.querySelector('[data-predictive-search]');
      const resultsContainer = container.querySelector('[data-search-results]');
      const loadingEl = container.querySelector('[data-search-loading]');
      const viewAllLink = container.querySelector('[data-search-view-all]');
      
      if (!input || !predictiveSearch || !resultsContainer) return;
      
      let debounceTimer;
      let abortController;
      
      const showPredictive = () => {
        predictiveSearch.setAttribute('aria-hidden', 'false');
      };
      
      const hidePredictive = () => {
        predictiveSearch.setAttribute('aria-hidden', 'true');
      };
      
      const showLoading = () => {
        loadingEl?.classList.add('is-loading');
        resultsContainer.innerHTML = '';
      };
      
      const hideLoading = () => {
        loadingEl?.classList.remove('is-loading');
      };
      
      const formatMoney = (price) => {
        // Shopify suggest.json returns price as float (e.g., 38.00), not cents
        const amount = parseFloat(price).toFixed(2).replace('.', ',');
        return `€${amount}`;
      };
      
      const renderResults = (products) => {
        if (!products || products.length === 0) {
          resultsContainer.innerHTML = `
            <div class="predictive-search__empty">
              <p>Geen producten gevonden</p>
            </div>
          `;
          return;
        }
        
        // Show max 6 products
        const items = products.slice(0, 6).map(product => {
          const image = product.featured_image?.url || product.image || '';
          const price = product.price;
          const comparePrice = product.compare_at_price_max;
          const isOnSale = comparePrice && comparePrice > price;
          
          // Rating (mock for now - integrate with reviews app later)
          const rating = product.metafields?.reviews?.rating?.value || 4.5;
          const ratingCount = product.metafields?.reviews?.rating_count?.value || Math.floor(Math.random() * 200) + 10;
          
          const stars = Array.from({length: 5}, (_, i) => {
            const fill = i < Math.floor(rating) ? '#FFE801' : (i < rating ? '#FFE801' : '#ddd');
            return `<svg width="14" height="14" viewBox="0 0 24 24" fill="${fill}"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>`;
          }).join('');
          
          return `
            <a href="${product.url}" class="predictive-search__item">
              ${image ? `<img src="${image}" alt="${product.title}" class="predictive-search__image" loading="lazy">` : '<div class="predictive-search__image predictive-search__image--placeholder"></div>'}
              <div class="predictive-search__info">
                <h4 class="predictive-search__title">${product.title}</h4>
                <div class="predictive-search__rating">
                  <span class="predictive-search__stars">${stars}</span>
                </div>
                <div class="predictive-search__price ${isOnSale ? 'predictive-search__price--sale' : ''}">
                  ${isOnSale ? `<s>${formatMoney(comparePrice)}</s>` : ''}
                  <strong>${formatMoney(price)}</strong>
                </div>
              </div>
            </a>
          `;
        }).join('');
        
        resultsContainer.innerHTML = items;
      };
      
      const performSearch = async (query) => {
        if (query.length < 1) {
          hidePredictive();
          return;
        }
        
        // Cancel previous request
        if (abortController) {
          abortController.abort();
        }
        abortController = new AbortController();
        
        showPredictive();
        showLoading();
        
        try {
          const response = await fetch(
            `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=hide`,
            { signal: abortController.signal }
          );
          
          if (!response.ok) throw new Error('Search failed');
          
          const data = await response.json();
          const products = data.resources?.results?.products || [];
          
          hideLoading();
          renderResults(products);
          
          // Update "view all" link
          if (viewAllLink) {
            viewAllLink.href = `/search?q=${encodeURIComponent(query)}&type=product`;
          }
          
        } catch (error) {
          if (error.name !== 'AbortError') {
            hideLoading();
            resultsContainer.innerHTML = '<div class="predictive-search__empty"><p>Er ging iets mis</p></div>';
          }
        }
      };
      
      // Event listeners
      input.addEventListener('input', (e) => {
        clearTimeout(debounceTimer);
        const query = e.target.value.trim();
        
        if (query.length === 0) {
          hidePredictive();
          return;
        }
        
        // Debounce: wait 150ms after typing stops
        debounceTimer = setTimeout(() => performSearch(query), 150);
      });
      
      input.addEventListener('focus', () => {
        const query = input.value.trim();
        if (query.length >= 1 && resultsContainer.innerHTML) {
          showPredictive();
        }
      });
      
      // Close on click outside
      document.addEventListener('click', (e) => {
        if (!container.contains(e.target)) {
          hidePredictive();
        }
      });
      
      // Close on Escape
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          hidePredictive();
          input.blur();
        }
      });
      
      // Clear button
      const clearBtn = container.querySelector('[data-search-clear]');
      if (clearBtn) {
        clearBtn.addEventListener('click', () => {
          input.value = '';
          hidePredictive();
          input.focus();
        });
      }
    });
  };

  // ==========================================================================
  // Quantity Selector (Generic)
  // ==========================================================================

  const initQuantitySelectors = () => {
    document.addEventListener('click', (e) => {
      // Skip cart drawer quantity buttons (handled separately)
      if (e.target.closest('[data-cart-quantity]')) return;
      
      const minusBtn = e.target.closest('[data-quantity-minus]');
      const plusBtn = e.target.closest('[data-quantity-plus]');

      if (minusBtn) {
        const input = minusBtn.parentElement.querySelector('[data-quantity-input]');
        if (input) {
          const value = parseInt(input.value) || 1;
          input.value = Math.max(1, value - 1);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      if (plusBtn) {
        const input = plusBtn.parentElement.querySelector('[data-quantity-input]');
        if (input) {
          const value = parseInt(input.value) || 1;
          const max = parseInt(input.max) || 99;
          input.value = Math.min(max, value + 1);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });
  };

  // ==========================================================================
  // Product Page - Gallery & Variants (PATNUTRITION.BE STYLE)
  // ==========================================================================

  const initProductPage = () => {
    const productSection = document.querySelector('[data-product]');
    if (!productSection) return;

    const productJson = productSection.querySelector('[data-product-json]');
    if (!productJson) return;

    const product = JSON.parse(productJson.textContent);
    let currentVariant = product.variants.find(v => v.id === parseInt(productSection.dataset.productId)) || product.variants[0];

    // ==========================================================================
    // Image Gallery - Swap src only, never replace DOM
    // ==========================================================================
    
    const mainImage = productSection.querySelector('[data-main-image]');
    const thumbButtons = productSection.querySelectorAll('[data-thumb-btn]');

    const switchMainImage = (thumb) => {
      if (!mainImage || !thumb) return;
      
      const newSrc = thumb.dataset.src;
      const newSrcset = thumb.dataset.srcset;
      const newAlt = thumb.dataset.alt;
      const newMediaId = thumb.dataset.mediaId;
      
      if (newSrc) {
        mainImage.src = newSrc;
        mainImage.srcset = newSrcset || '';
        mainImage.alt = newAlt || '';
        mainImage.dataset.mediaId = newMediaId;
      }
      
      thumbButtons.forEach(t => {
        t.classList.remove('is-active');
        t.setAttribute('aria-selected', 'false');
      });
      thumb.classList.add('is-active');
      thumb.setAttribute('aria-selected', 'true');
      
      // Auto-scroll thumbnail into view on mobile
      if (window.innerWidth < 768) {
        thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    };

    thumbButtons.forEach(thumb => {
      thumb.addEventListener('click', () => switchMainImage(thumb));
    });

    const switchToVariantImage = (variant) => {
      if (!variant || !variant.featured_media) return;
      
      const variantMediaId = String(variant.featured_media.id);
      const matchingThumb = productSection.querySelector(`[data-thumb-btn][data-media-id="${variantMediaId}"]`);
      
      if (matchingThumb) {
        switchMainImage(matchingThumb);
      }
    };

    // ==========================================================================
    // Variant Picker - Buttons & Dropdowns
    // ==========================================================================
    
    const variantIdInput = productSection.querySelector('[data-variant-id]');
    const quantityInput = productSection.querySelector('[data-quantity-input]');
    const optionButtons = productSection.querySelectorAll('[data-option-btn]');

    // Get selected options from buttons
    const getSelectedOptions = () => {
      const options = [];
      product.options.forEach((optionName, index) => {
        const selectedBtn = productSection.querySelector(`[data-option-btn][data-option-index="${index}"].is-selected`);
        if (selectedBtn) {
          options.push(selectedBtn.dataset.optionValue);
        } else {
          options.push(null);
        }
      });
      return options;
    };

    // Find variant by selected options
    const findVariant = (selectedOptions) => {
      if (selectedOptions.includes(null)) return null;
      return product.variants.find(v => 
        v.options.every((opt, i) => opt === selectedOptions[i])
      );
    };

    // Update variant selection
    const updateVariant = () => {
      const selectedOptions = getSelectedOptions();
      const variant = findVariant(selectedOptions);

      if (variant) {
        currentVariant = variant;
        
        if (variantIdInput) variantIdInput.value = variant.id;

        const url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);

        updatePrice(variant);
        updateAddButton(variant);
        switchToVariantImage(variant);
      } else {
        // No valid variant selected
        updateAddButton(null);
      }
    };

    // Handle button clicks
    optionButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.classList.contains('is-unavailable')) return;
        
        const optionIndex = btn.dataset.optionIndex;
        
        productSection.querySelectorAll(`[data-option-btn][data-option-index="${optionIndex}"]`).forEach(sibling => {
          sibling.classList.remove('is-selected');
          sibling.setAttribute('aria-selected', 'false');
        });
        
        btn.classList.add('is-selected');
        btn.setAttribute('aria-selected', 'true');
        
        updateVariant();
      });
    });

    // ==========================================================================
    // Price Updates
    // ==========================================================================

    const updatePrice = (variant) => {
      const priceWrapper = productSection.querySelector('[data-price-wrapper]');
      if (!priceWrapper) return;

      const price = priceWrapper.querySelector('[data-price]');
      const comparePrice = priceWrapper.querySelector('[data-compare-price]');

      if (price) {
        price.textContent = formatMoney(variant.price);
        price.classList.toggle('is-sale', variant.compare_at_price > variant.price);
      }

      if (comparePrice) {
        if (variant.compare_at_price > variant.price) {
          comparePrice.textContent = formatMoney(variant.compare_at_price);
          comparePrice.style.display = '';
        } else {
          comparePrice.style.display = 'none';
        }
      }
    };

    // ==========================================================================
    // Add to Cart Button
    // ==========================================================================

    const updateAddButton = (variant) => {
      const addBtn = productSection.querySelector('[data-add-to-cart]');
      const addText = productSection.querySelector('[data-add-text]');
      
      if (!addBtn) return;

      if (!variant) {
        addBtn.disabled = true;
        if (addText) addText.textContent = 'Kies eerst een optie';
      } else if (variant.available) {
        addBtn.disabled = false;
        if (addText) addText.textContent = 'In winkelwagen';
      } else {
        addBtn.disabled = true;
        if (addText) addText.textContent = 'Uitverkocht';
      }
    };

    // ==========================================================================
    // Form Submit
    // ==========================================================================

    const form = productSection.querySelector('[data-product-form]');
    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const addBtn = form.querySelector('[data-add-to-cart]');
        const loader = form.querySelector('.product-add-btn__loader');
        const variantId = form.querySelector('[data-variant-id]')?.value;
        const quantity = parseInt(productSection.querySelector('[data-quantity-input]')?.value) || 1;
        
        if (!variantId) {
          showNotification('Selecteer een variant', 'error');
          return;
        }

        // Collect line item properties (for bundles, customizations, etc.)
        // First, trigger the bundle selector to inject hidden inputs
        const bundleSelector = document.getElementById('bundle-selector');
        if (bundleSelector) {
          // Dispatch a custom event to let bundle-selector.js inject properties
          form.dispatchEvent(new Event('bundle-inject', { bubbles: true }));
        }

        // Now collect all properties from hidden inputs
        const properties = {};
        form.querySelectorAll('input[name^="properties["]').forEach(input => {
          // Extract property name from properties[Name] format
          const match = input.name.match(/properties\[([^\]]+)\]/);
          if (match && input.value) {
            properties[match[1]] = input.value;
          }
        });

        // Check for upsell selection
        const upsellSelect = document.getElementById('upsell-select');
        const upsellHandle = upsellSelect?.value || null;
        
        if (addBtn) addBtn.disabled = true;
        if (loader) loader.hidden = false;

        try {
          // Step 1: Add main product (without opening drawer yet)
          if (isSparkLayerActive()) {
            console.log('SparkLayer B2B active - skipping Shopify cart');
            return;
          }

          const mainItemData = {
            id: variantId,
            quantity: quantity
          };
          
          if (properties && Object.keys(properties).length > 0) {
            mainItemData.properties = properties;
          }

          const mainResponse = await fetch('/cart/add.js', {
            ...fetchConfig(),
            body: JSON.stringify({ items: [mainItemData] })
          });

          if (!mainResponse.ok) throw new Error('Failed to add main product');

          // Step 2: Add upsell if selected (silently)
          if (upsellHandle) {
            await addUpsellToCart(upsellHandle, quantity);
          }

          // Step 3: Update UI
          await updateCartCount();
          await refreshCartDrawer();
          
          if (window.cartDrawer) {
            window.cartDrawer.open();
          }

          showNotification('Toegevoegd aan winkelwagen!', 'success');

        } catch (error) {
          console.error(error);
          showNotification('Kon niet toevoegen aan winkelwagen', 'error');
        } finally {
          if (addBtn && currentVariant?.available) addBtn.disabled = false;
          if (loader) loader.hidden = true;
        }
      });
    }

    // ==========================================================================
    // Initialize
    // ==========================================================================

    if (currentVariant) {
      updatePrice(currentVariant);
      updateAddButton(currentVariant);
    }
  };

  // ==========================================================================
  // Cart Page
  // ==========================================================================

  const initCartPage = () => {
    const cartForm = document.querySelector('[data-cart-form]');
    if (!cartForm) return;

    // Quantity changes
    cartForm.addEventListener('change', debounce(async (e) => {
      if (!e.target.matches('[data-quantity-input]')) return;

      const key = e.target.dataset.key;
      const quantity = parseInt(e.target.value) || 0;

      await updateCartItem(key, quantity);
      // Reload page for cart page updates
      window.location.reload();
    }, 300));

    // Remove items
    document.addEventListener('click', async (e) => {
      const removeBtn = e.target.closest('[data-cart-form] [data-remove-item]');
      if (!removeBtn) return;

      e.preventDefault();
      const key = removeBtn.dataset.key;
      await updateCartItem(key, 0);
      window.location.reload();
    });
  };

  // ==========================================================================
  // Collection Filters
  // ==========================================================================

  const initCollectionFilters = () => {
    const filterForm = document.querySelector('[data-filter-form]');
    const filterToggle = document.querySelector('[data-filter-toggle]');
    const filterDrawer = document.querySelector('[data-filters]');
    const filterClose = document.querySelector('[data-filter-close]');
    const filterOverlay = document.querySelector('[data-filter-overlay]');
    const sortSelect = document.querySelector('[data-sort-select]');

    if (filterToggle && filterDrawer) {
      filterToggle.addEventListener('click', () => {
        filterDrawer.classList.toggle('is-open');
        filterToggle.setAttribute('aria-expanded', filterDrawer.classList.contains('is-open'));
      });

      filterClose?.addEventListener('click', () => {
        filterDrawer.classList.remove('is-open');
        filterToggle.setAttribute('aria-expanded', 'false');
      });

      filterOverlay?.addEventListener('click', () => {
        filterDrawer.classList.remove('is-open');
        filterToggle.setAttribute('aria-expanded', 'false');
      });
    }

    if (filterForm) {
      filterForm.addEventListener('change', debounce(() => {
        const formData = new FormData(filterForm);
        const params = new URLSearchParams();

        for (const [key, value] of formData.entries()) {
          if (value) params.append(key, value);
        }

        const url = `${window.location.pathname}?${params.toString()}`;
        window.location.href = url;
      }, 500));
    }

    if (sortSelect) {
      sortSelect.addEventListener('change', () => {
        const url = new URL(window.location);
        url.searchParams.set('sort_by', sortSelect.value);
        window.location.href = url.toString();
      });
    }
  };

  // ==========================================================================
  // Announcement Bar
  // ==========================================================================

  const initAnnouncementBar = () => {
    const messages = document.querySelectorAll('[data-announcement-messages] .announcement-bar__message');
    const closeBtn = document.querySelector('[data-announcement-close]');
    const bar = document.querySelector('[data-announcement-bar]');

    if (messages.length > 1) {
      let currentIndex = 0;
      
      setInterval(() => {
        messages[currentIndex].classList.remove('is-active');
        messages[currentIndex].setAttribute('aria-hidden', 'true');
        
        currentIndex = (currentIndex + 1) % messages.length;
        
        messages[currentIndex].classList.add('is-active');
        messages[currentIndex].setAttribute('aria-hidden', 'false');
      }, 5000);
    }

    if (closeBtn && bar) {
      closeBtn.addEventListener('click', () => {
        bar.style.display = 'none';
        sessionStorage.setItem('announcement-closed', 'true');
      });

      if (sessionStorage.getItem('announcement-closed') === 'true') {
        bar.style.display = 'none';
      }
    }
  };

  // ==========================================================================
  // Countdown Timer
  // ==========================================================================

  const initCountdown = () => {
    const countdowns = document.querySelectorAll('[data-countdown]');
    if (!countdowns.length) return;

    countdowns.forEach(countdown => {
      const endDate = new Date(countdown.dataset.end || countdown.dataset.countdown).getTime();

      const update = () => {
        const now = new Date().getTime();
        const distance = endDate - now;

        if (distance < 0) {
          countdown.style.display = 'none';
          return;
        }

        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);

        const daysEl = countdown.querySelector('[data-days], [data-countdown-days]');
        const hoursEl = countdown.querySelector('[data-hours], [data-countdown-hours]');
        const minutesEl = countdown.querySelector('[data-minutes], [data-countdown-minutes]');
        const secondsEl = countdown.querySelector('[data-seconds], [data-countdown-seconds]');

        if (daysEl) daysEl.textContent = String(days).padStart(2, '0');
        if (hoursEl) hoursEl.textContent = String(hours).padStart(2, '0');
        if (minutesEl) minutesEl.textContent = String(minutes).padStart(2, '0');
        if (secondsEl) secondsEl.textContent = String(seconds).padStart(2, '0');
      };

      update();
      setInterval(update, 1000);
    });
  };

  // ==========================================================================
  // Notifications
  // ==========================================================================

  const showNotification = (message, type = 'info') => {
    const container = document.getElementById('notifications');
    if (!container) {
      console.log(message);
      return;
    }

    const notification = document.createElement('div');
    notification.className = `notification notification--${type}`;
    notification.innerHTML = `
      <span>${message}</span>
      <button type="button" class="notification__close" aria-label="Sluiten">×</button>
    `;

    notification.querySelector('.notification__close')?.addEventListener('click', () => {
      notification.remove();
    });

    container.appendChild(notification);

    setTimeout(() => {
      notification.classList.add('notification--fade-out');
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  };

  // ==========================================================================
  // Money Formatting
  // ==========================================================================

  const formatMoney = (cents) => {
    // Shopify variant prices are in cents
    const amount = (parseInt(cents) / 100).toFixed(2).replace('.', ',');
    return `€${amount}`;
  };

  // ==========================================================================
  // Sticky Header
  // ==========================================================================

  const initStickyHeader = () => {
    const header = document.querySelector('[data-header][data-sticky]');
    if (!header) return;

    let lastScroll = 0;
    const threshold = 100;

    const handleScroll = () => {
      const currentScroll = window.scrollY;

      if (currentScroll > threshold) {
        header.classList.add('is-scrolled');
      } else {
        header.classList.remove('is-scrolled');
      }

      if (currentScroll > lastScroll && currentScroll > 300) {
        header.classList.add('is-hidden');
      } else {
        header.classList.remove('is-hidden');
      }

      lastScroll = currentScroll;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
  };

  // ==========================================================================
  // Initialize
  // ==========================================================================

  const init = () => {
    initMobileMenu();
    initCartDrawer();
    initPredictiveSearch();
    initQuantitySelectors();
    initProductPage();
    initCartPage();
    initCollectionFilters();
    initAnnouncementBar();
    initCountdown();
    initStickyHeader();
    initProductCardCart();
    initCartDrawerItems();
  };

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose utilities globally
  window.PatNutrition = {
    init,
    showNotification,
    updateCartCount,
    refreshCartDrawer,
    addToCart,
    formatMoney
  };

})();

